import { useState } from "react";

import { ImportOutlined, MenuVerticalFilled } from "@aircall/icons";
import {
  Banner,
  BannerHeading,
  BannerIcon,
  BannerSuffix,
  Box,
  Button,
  Dropdown,
  Flex,
  Menu,
  MenuItem,
  Tooltip,
} from "@aircall/tractor";
import { useTranslation } from "react-i18next";

import { useHeaderRules } from "@/hooks/useHeaderRules/useHeaderRules";
import { Title } from "../Title";

import { HeaderProps } from "./Header.decl";
import { useHandlers } from "./Header.hooks";

import { FeedbackButton } from "@/components/FeedbackButton";
import { HelpButton } from "@/components/HelpButton";
import { ShareLink } from "@/components/ShareLink/ShareLink";
import { useCall } from "@/hooks/useCall/useCall";
import { useBaseStore } from "@/store/store";

const DELAY = 200;

function InfoBanner() {
  const { t } = useTranslation();

  const { shouldDisplayInfoBanner } = useHeaderRules();
  const [showBanner, setShowBanner] = useState(shouldDisplayInfoBanner);

  const handleOnCloseBanner = () => {
    setShowBanner(false);
  };

  if (!showBanner) {
    return null;
  }

  return (
    <Banner variant="info">
      <BannerIcon />
      <BannerHeading>{t("conversation_lt_two_minutes")}</BannerHeading>
      <BannerSuffix>
        <Button
          variant="secondary"
          mode="ghost"
          size="xSmall"
          onClick={handleOnCloseBanner}
        >
          {t("close")}
        </Button>
      </BannerSuffix>
    </Banner>
  );
}

function PickActions({ mobile }: HeaderProps) {
  const { t } = useTranslation();

  const {
    handleOnClickShare,
    handleOnClickDownloadRecording,
    handleOnClickFeeback,
    handleOnClickHelp,
    handleOnClickDownloadTranscript,
  } = useHandlers();

  const audio = useBaseStore((store) => store.audio);
  const { call } = useCall();

  const hasAudio = Boolean(audio);
  const hasTranscript = Boolean(call?.transcription);

  if (mobile) {
    return (
      <Dropdown
        side="bottom"
        trigger={
          <Button mode="ghost" size="small" variant="secondary">
            <MenuVerticalFilled />
          </Button>
        }
      >
        <Menu>
          <MenuItem onClick={handleOnClickShare}>{t("copy_link")}</MenuItem>
          <MenuItem
            disabled={!hasAudio}
            onClick={handleOnClickDownloadRecording}
          >
            {t("download_audio")}
          </MenuItem>
          <MenuItem
            disabled={!hasTranscript}
            onClick={handleOnClickDownloadTranscript}
          >
            {t("download_transcript")}
          </MenuItem>
          <MenuItem onClick={handleOnClickHelp}>{t("help_center")}</MenuItem>
          <MenuItem onClick={handleOnClickFeeback}>
            {t("feedback_button")}
          </MenuItem>
        </Menu>
      </Dropdown>
    );
  }

  return (
    <Flex gap="xxxs" alignItems="center">
      <ShareLink />
      {(hasAudio || hasTranscript) && (
        <Tooltip mouseEnterDelay={DELAY} content={t("export")}>
          <Box>
            <Dropdown
              trigger={
                <Button
                  data-test="download-button"
                  variant="secondary"
                  mode="ghost"
                  size="small"
                >
                  <ImportOutlined />
                </Button>
              }
            >
              <Menu>
                <MenuItem
                  data-test="download-audio-button"
                  disabled={!hasAudio}
                  onClick={handleOnClickDownloadRecording}
                >
                  {t("download_audio")}
                </MenuItem>
                <MenuItem
                  data-test="download-transcript-button"
                  disabled={!hasTranscript}
                  onClick={handleOnClickDownloadTranscript}
                >
                  {t("download_transcript")}
                </MenuItem>
              </Menu>
            </Dropdown>
          </Box>
        </Tooltip>
      )}
      <HelpButton />
      <FeedbackButton event="assets_page_feedback_button_clicked" />
    </Flex>
  );
}

export function Header({ mobile }: HeaderProps) {
  const { call } = useCall();

  if (!call) {
    return null;
  }

  const hasParticipants = !!call.from && !!call.to;

  return (
    <Flex flexDirection="column">
      <InfoBanner />
      <Flex
        justifyContent="space-between"
        alignItems="center"
        px="m"
        py="xs"
        borderBottom="0.0625rem solid"
        borderBottomColor="graphic-default"
        data-test="call-details-container"
        backgroundColor="surface-default"
      >
        <Flex alignItems="center">
          {hasParticipants && (
            <Title
              type={call.type}
              from={call.from}
              to={call.to}
              speakers={call.speakers}
            />
          )}
        </Flex>
        <PickActions mobile={mobile} />
      </Flex>
    </Flex>
  );
}
